// Centralized error handling system for production-ready error management

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export enum ErrorCategory {
  AUTHENTICATION = 'authentication',
  NETWORK = 'network',
  DATABASE = 'database',
  VALIDATION = 'validation',
  PERMISSION = 'permission',
  FILE_UPLOAD = 'file_upload',
  USER_INPUT = 'user_input',
  SYSTEM = 'system',
  UNKNOWN = 'unknown'
}

export interface ErrorContext {
  userId?: string;
  action?: string;
  component?: string;
  timestamp: string;
  userAgent?: string;
  url?: string;
  errorBoundary?: boolean;
  componentStack?: string;
  errorBoundaryProps?: unknown;
  additionalData?: Record<string, unknown>;
}

export interface AppError {
  id: string;
  message: string;
  originalError?: Error;
  severity: ErrorSeverity;
  category: ErrorCategory;
  context: ErrorContext;
  stack?: string;
  isUserFacing: boolean;
}

class ErrorHandler {
  private static instance: ErrorHandler;
  private errorQueue: AppError[] = [];
  private isProduction = process.env.NODE_ENV === 'production';

  private constructor() {
    // Set up global error handlers
    if (typeof window !== 'undefined') {
      window.addEventListener('error', this.handleGlobalError.bind(this));
      window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
    }
  }

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private handleGlobalError(event: ErrorEvent): void {
    this.captureError(new Error(event.message), {
      severity: ErrorSeverity.HIGH,
      category: ErrorCategory.SYSTEM,
      context: {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        additionalData: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      }
    });
  }

  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    this.captureError(error, {
      severity: ErrorSeverity.HIGH,
      category: ErrorCategory.SYSTEM,
      context: {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        additionalData: { type: 'unhandled_promise_rejection' }
      }
    });
  }

  captureError(
    error: Error | string,
    options: {
      severity?: ErrorSeverity;
      category?: ErrorCategory;
      context?: Partial<ErrorContext>;
      isUserFacing?: boolean;
    } = {}
  ): AppError {
    const errorInstance = typeof error === 'string' ? new Error(error) : error;
    
    const appError: AppError = {
      id: this.generateErrorId(),
      message: errorInstance.message,
      originalError: errorInstance,
      severity: options.severity || ErrorSeverity.MEDIUM,
      category: options.category || ErrorCategory.UNKNOWN,
      context: {
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof window !== 'undefined' ? navigator.userAgent : '',
        ...options.context
      },
      stack: errorInstance.stack,
      isUserFacing: options.isUserFacing ?? true
    };

    // Add to error queue
    this.errorQueue.push(appError);

    // Log error based on environment
    if (this.isProduction) {
      this.reportToExternalService(appError);
    } else {
      this.logToConsole(appError);
    }

    return appError;
  }

  private logToConsole(error: AppError): void {
    const logLevel = this.getConsoleLogLevel(error.severity);
    const logMessage = `[${error.severity.toUpperCase()}] ${error.category}: ${error.message}`;
    
    console.group(`🚨 Error ${error.id}`);
    console[logLevel](logMessage);
    if (error.originalError) {
      console.error('Original Error:', error.originalError);
    }
    if (error.context.additionalData) {
      console.table(error.context.additionalData);
    }
    console.groupEnd();
  }

  private getConsoleLogLevel(severity: ErrorSeverity): 'error' | 'warn' | 'info' {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.HIGH:
        return 'error';
      case ErrorSeverity.MEDIUM:
        return 'warn';
      case ErrorSeverity.LOW:
        return 'info';
      default:
        return 'error';
    }
  }

  private reportToExternalService(error: AppError): void {
    // In production, you would send to services like Sentry, LogRocket, etc.
    // For now, we'll use a simple fetch to a logging endpoint
    if (typeof window !== 'undefined') {
      fetch('/api/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(error)
      }).catch(() => {
        // Silently fail - don't want error reporting to break the app
      });
    }
  }

  // Method to get user-friendly error messages
  getUserFriendlyMessage(error: AppError): string {
    const baseMessages = {
      [ErrorCategory.AUTHENTICATION]: "There was an issue with authentication. Please try signing in again.",
      [ErrorCategory.NETWORK]: "Connection problem. Please check your internet connection and try again.",
      [ErrorCategory.DATABASE]: "We're having trouble saving your data. Please try again in a moment.",
      [ErrorCategory.VALIDATION]: "Please check your input and try again.",
      [ErrorCategory.PERMISSION]: "You don't have permission to perform this action.",
      [ErrorCategory.FILE_UPLOAD]: "There was a problem uploading your file. Please try again.",
      [ErrorCategory.USER_INPUT]: "Please check your input and try again.",
      [ErrorCategory.SYSTEM]: "Something went wrong. Our team has been notified.",
      [ErrorCategory.UNKNOWN]: "An unexpected error occurred. Please try again."
    };

    return baseMessages[error.category] || baseMessages[ErrorCategory.UNKNOWN];
  }

  // Get recent errors for debugging
  getRecentErrors(count = 10): AppError[] {
    return this.errorQueue.slice(-count);
  }

  // Clear error queue (useful for testing)
  clearErrors(): void {
    this.errorQueue = [];
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();

// Convenience functions for common error scenarios
export const captureAuthError = (error: Error | string, context?: Partial<ErrorContext>) =>
  errorHandler.captureError(error, {
    severity: ErrorSeverity.HIGH,
    category: ErrorCategory.AUTHENTICATION,
    context
  });

export const captureNetworkError = (error: Error | string, context?: Partial<ErrorContext>) =>
  errorHandler.captureError(error, {
    severity: ErrorSeverity.MEDIUM,
    category: ErrorCategory.NETWORK,
    context
  });

export const captureDatabaseError = (error: Error | string, context?: Partial<ErrorContext>) =>
  errorHandler.captureError(error, {
    severity: ErrorSeverity.HIGH,
    category: ErrorCategory.DATABASE,
    context
  });

export const captureValidationError = (error: Error | string, context?: Partial<ErrorContext>) =>
  errorHandler.captureError(error, {
    severity: ErrorSeverity.LOW,
    category: ErrorCategory.VALIDATION,
    context,
    isUserFacing: true
  });

export const captureFileUploadError = (error: Error | string, context?: Partial<ErrorContext>) =>
  errorHandler.captureError(error, {
    severity: ErrorSeverity.MEDIUM,
    category: ErrorCategory.FILE_UPLOAD,
    context
  });

export const captureSystemError = (error: Error | string, context?: Partial<ErrorContext>) =>
  errorHandler.captureError(error, {
    severity: ErrorSeverity.CRITICAL,
    category: ErrorCategory.SYSTEM,
    context
  });